import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Button,
  Link,
  InputAdornment,
} from '@mui/material';
import { calculateOrderTotals } from '../../utils/taxCalc';
import { formatCurrency } from '../../utils/formatCurrency';
import { toast } from '../../lib/toast';

/**
 * MUI-native menu settings form for AdminMenuV2's "Menu Settings" tab.
 * Controlled/presentational — mirrors the props of the classic
 * components/Admin/SettingsTab.jsx so it shares the same load/save wiring.
 */
export default function MenuSettingsPanel({
  restaurantName,
  onRestaurantNameChange,
  restaurantSlug = '',
  onRestaurantSlugChange,
  isAdmin,
  currencies,
  restaurantCurrency,
  onCurrencyChange,
  taxConfig,
  onTaxConfigChange,
  requireCustomerOtp = false,
  onRequireCustomerOtpChange,
  saving,
  onSubmit,
}) {
  const { t } = useTranslation();

  const tax = useMemo(
    () => taxConfig || { tax_enabled: false, tax_rate: 0, tax_inclusive: true, tax_label: 'VAT', tax_number: '' },
    [taxConfig]
  );
  const updateTax = (patch) => onTaxConfigChange && onTaxConfigChange({ ...tax, ...patch });

  const previewBase = 100;
  const preview = useMemo(
    () => calculateOrderTotals([{ price: previewBase, quantity: 1 }], tax),
    [tax]
  );
  const taxLabel = (tax.tax_label || 'VAT').trim() || 'VAT';

  const menuLink = restaurantSlug ? `${window.location.origin}/menu/${restaurantSlug}` : '';
  const copyMenuLink = async () => {
    if (!menuLink) return;
    try {
      await navigator.clipboard.writeText(menuLink);
      toast.success(t('admin.slug.copied'));
    } catch (_) {
      /* clipboard unavailable — no-op */
    }
  };

  const handleCurrencyChange = (code) => {
    const c = (currencies || []).find((cu) => cu.code === code) || { code: 'USD', symbol: '$', position: 'before' };
    onCurrencyChange({ code: c.code, symbol: c.symbol, position: c.position || 'before' });
  };

  const sectionTitle = (icon, label) => (
    <Typography sx={{ fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      <span aria-hidden="true">{icon}</span>
      {label}
    </Typography>
  );

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, borderRadius: 2 }}>
      <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          ⚙️ {t('admin.settings.title')}
        </Typography>

        {/* Restaurant identity */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            size="small"
            label={t('admin.restaurantName')}
            value={restaurantName || ''}
            onChange={(e) => onRestaurantNameChange(e.target.value)}
            placeholder={t('admin.addMenuItem.itemNamePlaceholder')}
            disabled={!isAdmin}
            fullWidth
          />
          <Box>
            <TextField
              size="small"
              label={t('admin.slug.label')}
              value={restaurantSlug}
              onChange={(e) => onRestaurantSlugChange && onRestaurantSlugChange(e.target.value)}
              placeholder={t('admin.slug.placeholder')}
              disabled={!isAdmin}
              helperText={isAdmin ? t('admin.slug.help') : t('admin.slug.adminOnly')}
              sx={{ maxWidth: 360 }}
              fullWidth
            />
            {menuLink && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                  {t('admin.slug.menuLinkLabel')}:
                </Typography>
                <Link href={menuLink} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 13 }}>
                  {menuLink}
                </Link>
                <Button size="small" variant="outlined" onClick={copyMenuLink} sx={{ textTransform: 'none' }}>
                  {t('admin.slug.copy')}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {/* Currency */}
        <Box>
          <TextField
            select
            size="small"
            label={t('admin.currency.label')}
            value={restaurantCurrency.code}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            sx={{ maxWidth: 360 }}
            fullWidth
          >
            {(currencies || []).length === 0 && (
              <MenuItem value="USD">USD — US Dollar ($14.99)</MenuItem>
            )}
            {(currencies || []).map((c) => (
              <MenuItem key={c.code} value={c.code}>
                {c.code} — {c.name} ({c.example})
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75 }}>
            {t('admin.currency.preview')}: {formatCurrency(14.99, restaurantCurrency)}
          </Typography>
        </Box>

        {/* Tax */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            {sectionTitle('🧾', t('admin.tax.section'))}
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={Boolean(tax.tax_enabled)}
                  onChange={(e) => updateTax({ tax_enabled: e.target.checked })}
                />
              }
              label={t('admin.tax.enabled')}
            />
          </Box>

          {!tax.tax_enabled ? (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
              💤 {t('admin.tax.disabledNotice')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  type="number"
                  label={t('admin.tax.rate')}
                  value={tax.tax_rate}
                  onChange={(e) => updateTax({ tax_rate: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, max: 100, step: '0.01' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  sx={{ width: 160 }}
                />
                <TextField
                  size="small"
                  label={t('admin.tax.label')}
                  value={tax.tax_label}
                  onChange={(e) => updateTax({ tax_label: e.target.value })}
                  inputProps={{ maxLength: 50 }}
                  placeholder="VAT"
                  helperText={t('admin.tax.labelHelp')}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>

              <FormControl>
                <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{t('admin.tax.mode')}</FormLabel>
                <RadioGroup
                  value={tax.tax_inclusive ? 'inclusive' : 'exclusive'}
                  onChange={(e) => updateTax({ tax_inclusive: e.target.value === 'inclusive' })}
                >
                  <FormControlLabel
                    value="inclusive"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('admin.tax.modeInclusive')}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('admin.tax.modeInclusiveHint')}</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="exclusive"
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('admin.tax.modeExclusive')}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('admin.tax.modeExclusiveHint')}</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                size="small"
                label={t('admin.tax.number')}
                value={tax.tax_number || ''}
                onChange={(e) => updateTax({ tax_number: e.target.value })}
                inputProps={{ maxLength: 50 }}
                placeholder="—"
                helperText={t('admin.tax.numberHelp')}
                sx={{ maxWidth: 360 }}
                fullWidth
              />

              {preview.tax_active && preview.tax_amount > 0 && (
                <Box
                  aria-live="polite"
                  sx={{ backgroundColor: 'action.hover', borderRadius: 1.5, p: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{t('admin.tax.preview')}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {t('admin.tax.previewHint', { base: formatCurrency(previewBase, restaurantCurrency) })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'text.secondary' }}>
                    <span>{t('tax.subtotal')}</span>
                    <span>{formatCurrency(preview.subtotal, restaurantCurrency)}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
                    <span>
                      {taxLabel} ({preview.tax_rate}%)
                      {preview.tax_inclusive ? t('tax.includedSuffix') : ''}
                    </span>
                    <span>{formatCurrency(preview.tax_amount, restaurantCurrency)}</span>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, mt: 0.75 }}>
                    <span>{t('tax.total')}</span>
                    <span>{formatCurrency(preview.total, restaurantCurrency)}</span>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Customer OTP */}
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            {sectionTitle('📱', t('admin.customerAuth.section'))}
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={Boolean(requireCustomerOtp)}
                  onChange={(e) => onRequireCustomerOtpChange && onRequireCustomerOtpChange(e.target.checked)}
                />
              }
              label={t('admin.customerAuth.requireOtp')}
            />
          </Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
            {requireCustomerOtp ? '🔒' : '💤'}{' '}
            {requireCustomerOtp
              ? t('admin.customerAuth.enabledNotice')
              : t('admin.customerAuth.disabledNotice')}
          </Typography>
        </Box>

        <Box>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? t('common.saving', 'Saving...') : t('admin.settings.save')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
